import { SubFlowIcon } from "@signet/ui";
import { CreatorAvatar, NowStat, nowDay, nowRangeVersus, nowRangeWords, submittedWords, useCreatorPhotos, useSeenQuery, type NowRange } from "@signet/web-kit";
import { BASE, fetchRange, shiftDay, subsKeys, type Flow } from "./api.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Sub flow beside Visitors, Clicks and Revenue on Now, as the boss drew it
   (2026-10-13): the subs in the period, the change against the period
   before, and across the foot "Top 3" with each creator's photo, name and
   total across their pages. No line: Jay took it off the same day.

   Today shows the newest day with subs typed in, even when that is yesterday
   (Jay, 2026-10-15), unlike every other figure on Now. Clicking opens Sub Flow
   over the same days. Shares the panel's request.
   ────────────────────────────────────────────────────────────────────────── */

/**
 * The day after "Sub flow": Today shows the newest day typed in, which is almost always yesterday,
 * so both Today and Yesterday say the day they show (Jay, 2026-10-15). Before it loads, yesterday.
 */
export function flowWords(range: NowRange, flow: Flow | undefined): string {
  if (range.key !== "today" && range.key !== "yesterday") return nowRangeWords(range);
  if (!flow || flow.to === flow.latest) return "yesterday";
  return flow.to === shiftDay(flow.latest, 1) ? "today" : nowDay(flow.to);
}

const noData = (range: NowRange) =>
  range.key === "today" ? "No data for today" : range.key === "yesterday" ? "No data for yesterday" : range.from && range.from === range.to ? "No data for this day" : "No data for these days";

export function SubsNowStat({ range }: { range: NowRange }) {
  const flow = useSeenQuery({ queryKey: subsKeys.range(range), queryFn: () => fetchRange(range), refetchInterval: 60_000 });
  const photos = useCreatorPhotos(BASE);
  const data = flow.data;
  const empty = Boolean(data?.noData);
  const top = data && !empty ? data.rows.slice(0, 3) : [];

  return (
    <NowStat
      className="subs-stat"
      icon={<SubFlowIcon size={18} />}
      label={`Sub flow ${flowWords(range, data)}`}
      // When the day's numbers were last saved, for Today and Yesterday only (Jay, 2026-10-15).
      note={!empty && (range.key === "today" || range.key === "yesterday") ? submittedWords(data?.updatedAt) : null}
      colour="var(--good)"
      value={!data ? null : empty ? noData(range) : data.totals.total.toLocaleString()}
      empty={empty}
      current={empty ? null : data?.totals.total}
      previous={empty ? null : data?.previousTotal}
      versus={nowRangeVersus(range)}
      failed={flow.isError}
      href="/subs"
      // Sub Flow over the same days (Jay, 2026-10-13): one day as ?day=, more as ?from=&to=.
      search={data ? (data.from === data.to ? { day: data.from } : { from: data.from, to: data.to }) : undefined}
      footer={
        top.length ? (
          <div className="subs-stat-foot">
            <span className="subs-stat-foot-label">Top 3</span>
            <ol>
              {top.map((r) => (
                <li key={r.id}>
                  <span className="subs-stat-photo"><CreatorAvatar name={r.creator} src={photos(r.creator)} size={24} /></span>
                  <span>
                    <small>
                      {r.creator}
                      {r.platform === "fansly" ? " (Fansly)" : ""}
                    </small>
                    <b className="tabular">{r.total.toLocaleString()}</b>
                  </span>
                </li>
              ))}
            </ol>
          </div>
        ) : null
      }
    />
  );
}
